import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, MapPin, Phone, Mail, User,
  PlayCircle, Power, RefreshCw, Trash2, RotateCcw,
  FileCheck, FileX, CheckCircle, Loader2, AlertTriangle,
  Zap, XCircle
} from 'lucide-react';
import WorkflowContainer from '../components/WorkflowContainer';
import OffboardingWorkflowPanel from '../components/OffboardingWorkflowPanel';
import ActivityLog from '../components/ActivityLog';
import ReonboardCard from '../components/ReonboardCard';
import { 
  LIFECYCLE_STATUS, 
  LIFECYCLE_STATUS_LABELS, 
  LIFECYCLE_STATUS_COLORS,
  ONBOARDING_STEPS,
  OFFBOARDING_STEPS,
  STEP_STATUS,
  PROPERTY_STATUS
} from '../constants';
import { lifecycleAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { userHasRole } from '../../../utils/roleHelpers';

const HotelLifecycleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  // Phase 15.4 — `activeWorkflow` is now DERIVED from the hotel's lifecycle
  // state (see useEffect below), not hard-coded to 'onboarding'. Otherwise
  // any handler that fires lifecycleAPI.startStep/completeStep/assignUser
  // with `activeWorkflow='onboarding'` would mutate onboarding state even
  // when the property is in the offboarding phase.
  const [activeWorkflow, setActiveWorkflow] = useState('onboarding');
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading

  const isAdmin = userHasRole(user, 'admin');
  const role = (user?.role || '').toLowerCase();

  useEffect(() => {
    loadHotelData();
    loadUsers();
  }, [id]);

  // Phase 15.4 — derive activeWorkflow from the hotel lifecycle state so any
  // step handler (start/complete/assign) targets the CORRECT workflow.
  // Without this the user could be staring at the Offboarding workflow but
  // clicking "Start step" would silently mutate `onboardingSteps`.
  useEffect(() => {
    if (!hotel) return;
    const isOffboardingState =
      hotel.propertyStatus === 'Offboarding' ||
      hotel.propertyStatus === 'Inactive' ||
      hotel.propertyStatus === 'Offboarded' ||
      hotel.lifecycleStatus === LIFECYCLE_STATUS.OFFBOARDING_IN_PROGRESS ||
      hotel.lifecycleStatus === LIFECYCLE_STATUS.OFFBOARDED ||
      hotel.lifecycleStatus === LIFECYCLE_STATUS.DISCONTINUED ||
      hotel.propertyStatus === 'Discontinued';
    setActiveWorkflow(isOffboardingState ? 'offboarding' : 'onboarding');
  }, [hotel?.propertyStatus, hotel?.lifecycleStatus]);

  const loadHotelData = async () => {
    try {
      setLoading(true);
      const response = await lifecycleAPI.getById(id);
      setHotel(response.data);
      setActivities(response.data?.activities || []);
    } catch (error) {
      console.error('Error loading hotel:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await lifecycleAPI.getUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleStartStep = async (stepId) => {
    try {
      await lifecycleAPI.startStep(id, activeWorkflow, stepId);
      await loadHotelData();
    } catch (error) {
      console.error('Error starting step:', error);
      alert('Failed to start step');
    }
  };

  const handleCompleteStep = async (stepId, notes) => {
    try {
      await lifecycleAPI.completeStep(id, activeWorkflow, stepId, notes);
      await loadHotelData();
    } catch (error) {
      console.error('Error completing step:', error);
      alert('Failed to complete step');
    }
  };

  const handleAssignUser = async (stepId, userId) => {
    try {
      await lifecycleAPI.assignUser(id, activeWorkflow, stepId, userId);
      await loadHotelData();
    } catch (error) {
      console.error('Error assigning user:', error);
      alert('Failed to assign user');
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setActionLoading('startOnboarding');
      await lifecycleAPI.startOnboarding(id);
      await loadHotelData();
    } catch (error) {
      console.error('Error starting onboarding:', error);
      alert('Failed to start onboarding');
    } finally {
      setActionLoading(null);
    }
  };

  // Admin Action: Final Go Live
  const handleFinalGoLive = async () => {
    if (!window.confirm('Are you sure you want to mark this property as Live? All onboarding steps must be completed.')) return;
    
    try {
      setActionLoading('finalGoLive');
      await lifecycleAPI.finalGoLive(id);
      await loadHotelData();
      alert('Property is now Live!');
    } catch (error) {
      console.error('Error completing Final Go Live:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to complete Final Go Live';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Admin Action: Start Offboarding — Phase 15.1
  // Prompts for reason (backend requires it). On success, redirects to the
  // dedicated Offboarding Workflow page with this hotel pre-selected so the
  // user immediately lands on Step 1 (Request Initiated) marked completed,
  // with the full 7-step workflow visible. Previously, setting activeWorkflow
  // had no visual effect because this detail page only renders the Onboarding
  // workflow container.
  const handleStartOffboarding = async () => {
    const reason = window.prompt(
      'Please enter the reason for offboarding this property:\n\n(This will start the 7-step offboarding workflow.)',
      ''
    );
    if (reason === null) return;
    const trimmed = (reason || '').trim();
    if (!trimmed) {
      alert('Reason is required to start offboarding.');
      return;
    }

    try {
      setActionLoading('startOffboarding');
      const resp = await lifecycleAPI.startOffboarding(id, { reason: trimmed });
      const targetId = resp?.data?.lifecycle_id || id;
      // Phase 15.2 — URL-param route so refresh/back/forward all keep
      // the user on the 7-step offboarding workflow. `location.state` is
      // still passed for back-compat but is no longer the source of truth.
      navigate(`/hotel-lifecycle/offboarding/${targetId}`, {
        state: { autoSelectHotelId: targetId, propertyId: id },
      });
    } catch (error) {
      console.error('Error starting offboarding:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to start offboarding';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Admin Action: Complete Offboarding
  const handleCompleteOffboarding = async () => {
    if (!window.confirm('Are you sure you want to complete offboarding? This will mark the property as Discontinued and update the Lead to DISCONTINUED stage.')) return;
    
    try {
      setActionLoading('completeOffboarding');
      await lifecycleAPI.completeOffboarding(id);
      await loadHotelData();
      alert('Offboarding completed. Property is now Discontinued.');
    } catch (error) {
      console.error('Error completing offboarding:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to complete offboarding';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Check if all onboarding steps are completed
  const areAllOnboardingStepsCompleted = () => {
    const steps = hotel?.onboardingSteps || {};
    return ONBOARDING_STEPS.every(step => steps[step.id]?.status === STEP_STATUS.COMPLETED);
  };

  // Check if all offboarding steps are completed
  const areAllOffboardingStepsCompleted = () => {
    const steps = hotel?.offboardingSteps || {};
    return OFFBOARDING_STEPS.every(step => steps[step.id]?.status === STEP_STATUS.COMPLETED);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading hotel data...</p>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="text-center py-12">
        <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Hotel Not Found</h2>
        <p className="text-gray-500 mb-4">The hotel you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/lifecycle')}
          className="text-teal-600 hover:text-teal-700 font-medium"
        >
          Back to Lifecycle Dashboard
        </button>
      </div>
    );
  }

  // Determine lifecycle and property status
  const lifecycleStatus = hotel.lifecycleStatus || LIFECYCLE_STATUS.NOT_ONBOARDED;
  const propertyStatus = hotel.propertyStatus || PROPERTY_STATUS.ONBOARDING;
  const colors = LIFECYCLE_STATUS_COLORS[lifecycleStatus] || LIFECYCLE_STATUS_COLORS[LIFECYCLE_STATUS.NOT_ONBOARDED];

  // UI state logic
  const canStartOnboarding = lifecycleStatus === LIFECYCLE_STATUS.NOT_ONBOARDED;
  const isOnboardingInProgress = lifecycleStatus === LIFECYCLE_STATUS.ONBOARDING_IN_PROGRESS || propertyStatus === 'Onboarding';
  const isLive = hotel.finalGoLiveStatus || propertyStatus === 'Live' || propertyStatus === 'Onboarded' || lifecycleStatus === LIFECYCLE_STATUS.LIVE;
  const isOffboarding = propertyStatus === 'Offboarding' || lifecycleStatus === LIFECYCLE_STATUS.OFFBOARDING_IN_PROGRESS;
  const isDiscontinued = propertyStatus === 'Discontinued' || lifecycleStatus === LIFECYCLE_STATUS.DISCONTINUED;
  // Phase 19.1 — Fully offboarded (terminal) state. Backend `complete-offboarding`
  // sets `lifecycleStatus='offboarded'` + `propertyStatus='Inactive'` (and the
  // `Offboarded` value is also possible from older code paths). We treat this
  // as its own state so the detail page renders the OffboardingWorkflowPanel
  // with all 7 steps + the ReonboardCard.
  const isOffboarded = lifecycleStatus === LIFECYCLE_STATUS.OFFBOARDED ||
    propertyStatus === 'Inactive' || propertyStatus === 'Offboarded';
  
  const canTriggerFinalGoLive = isAdmin && isOnboardingInProgress && areAllOnboardingStepsCompleted() && !hotel.finalGoLiveStatus;
  // Phase 15.1 — Start Offboarding is open to cx, cx_leader, gtm_sales,
  // gtm_sales_leader and admin (per backend role gate on start_offboarding).
  // It is gated by the property being live/onboarded (isLive).
  const canStartOffboarding = isLive && (
    isAdmin ||
    role === 'cx' || role === 'cx_leader' ||
    role === 'gtm_sales' || role === 'gtm_sales_leader'
  );
  const canCompleteOffboarding = isAdmin && isOffboarding && areAllOffboardingStepsCompleted();

  // Phase 15.4 — hide the "Onboarding Workflow" header tab when offboarding
  // is in progress / discontinued so the page never shows two conflicting
  // workflow labels at once. The actual workflow card below is also
  // switched to OFFBOARDING_STEPS in this state.
  const showOnboardingWorkflow = !isDiscontinued && !isOffboarding && !isOffboarded;
  const showOffboardingWorkflow = isOffboarding || isDiscontinued || isOffboarded;

  // Get the property status color - Updated for new statuses (Onboarding, Onboarded, Offboarding)
  const getPropertyStatusColor = () => {
    if (hotel.finalGoLiveStatus && !isOffboarded && !isOffboarding && !isDiscontinued) return 'bg-green-100 text-green-700 border-green-300';
    if (isOffboarded) return 'bg-slate-100 text-slate-700 border-slate-300';
    switch (propertyStatus) {
      case 'Onboarded':
      case 'Live':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'Active':
      case 'Onboarding': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Offboarding': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'Discontinued': return 'bg-red-100 text-red-700 border-red-300';
      case 'Inactive':
      case 'Offboarded': return 'bg-slate-100 text-slate-700 border-slate-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Normalize property status display
  const getDisplayStatus = () => {
    if (isOffboarded) return 'Offboarded — 100%';
    if (hotel.finalGoLiveStatus) return 'Onboarded';
    if (propertyStatus === 'Live') return 'Onboarded';
    if (propertyStatus === 'Active') return 'Onboarding';
    return propertyStatus;
  };

  return (
    <div className="space-y-6" data-testid="lifecycle-detail">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/lifecycle')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-2xl shadow-sm">
            {hotel.propertyName?.charAt(0) || 'H'}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-800">{hotel.propertyName}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPropertyStatusColor()}`}>
                {getDisplayStatus()}
              </span>
              {hotel.finalGoLiveStatus && !isOffboarding && !isDiscontinued && !isOffboarded && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500 text-white flex items-center gap-1">
                  <CheckCircle size={14} /> Onboarded
                </span>
              )}
              {/* Phase 19.1 — show "Re-onboarded" badge when the lifecycle has been
                  restored from a previous offboarding via the 3-step workflow. */}
              {hotel.reonboarded_at && !isOffboarded && !isOffboarding && !isDiscontinued && (
                <span
                  className="px-3 py-1 rounded-full text-xs font-medium bg-teal-500 text-white flex items-center gap-1"
                  data-testid="reonboarded-badge"
                >
                  <RotateCcw size={14} /> Re-onboarded
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <span>{hotel.city}{hotel.state ? `, ${hotel.state}` : ''}</span>
              </div>
              {hotel.mobile && (
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>{hotel.mobile}</span>
                </div>
              )}
              {hotel.email && (
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span>{hotel.email}</span>
                </div>
              )}
              {hotel.ownerName && (
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>{hotel.ownerName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            {canStartOnboarding && (
              <button
                onClick={handleStartOnboarding}
                disabled={actionLoading === 'startOnboarding'}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50"
                data-testid="start-onboarding-btn"
              >
                {actionLoading === 'startOnboarding' ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <PlayCircle size={18} />
                )}
                Start Onboarding
              </button>
            )}
            
            <button
              onClick={loadHotelData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
              data-testid="refresh-btn"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Admin/Role Action Panel — Start Offboarding is open to cx/cx_leader/gtm_sales/gtm_sales_leader; other actions remain admin-only */}
      {(isAdmin || canStartOffboarding) && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Zap size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">{isAdmin ? 'Admin Actions' : 'Workflow Actions'}</h3>
              <p className="text-sm text-gray-500">{isAdmin ? 'Critical workflow actions (Admin only)' : 'Start the offboarding workflow for this property'}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {/* Final Go Live Button — Admin only */}
            {isAdmin && isOnboardingInProgress && !hotel.finalGoLiveStatus && (
              <button
                onClick={handleFinalGoLive}
                disabled={!areAllOnboardingStepsCompleted() || actionLoading === 'finalGoLive'}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
                  areAllOnboardingStepsCompleted()
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                title={areAllOnboardingStepsCompleted() ? 'Mark property as Live' : 'Complete all onboarding steps first'}
                data-testid="final-go-live-btn"
              >
                {actionLoading === 'finalGoLive' ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Final Go Live
              </button>
            )}

            {/* Start Offboarding Button — visible to cx/cx_leader/gtm_sales/gtm_sales_leader/admin when property is Live */}
            {canStartOffboarding && (
              <button
                onClick={handleStartOffboarding}
                disabled={actionLoading === 'startOffboarding'}
                className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium shadow-sm disabled:opacity-50"
                data-testid="start-offboarding-btn"
              >
                {actionLoading === 'startOffboarding' ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Power size={18} />
                )}
                Start Offboarding
              </button>
            )}

            {/* Complete Offboarding Button — Admin only */}
            {isAdmin && isOffboarding && (
              <button
                onClick={handleCompleteOffboarding}
                disabled={!areAllOffboardingStepsCompleted() || actionLoading === 'completeOffboarding'}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
                  areAllOffboardingStepsCompleted()
                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                title={areAllOffboardingStepsCompleted() ? 'Mark property as Discontinued' : 'Complete all offboarding steps first'}
                data-testid="complete-offboarding-btn"
              >
                {actionLoading === 'completeOffboarding' ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <XCircle size={18} />
                )}
                Complete Offboarding
              </button>
            )}

            {/* Status Indicators */}
            {hotel.finalGoLiveStatus && !isOffboarding && !isDiscontinued && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg">
                <CheckCircle size={18} />
                <span className="font-medium">Property Onboarded</span>
                {hotel.finalGoLiveAt && (
                  <span className="text-xs text-green-600 ml-2">
                    since {new Date(hotel.finalGoLiveAt).toLocaleDateString('en-IN')}
                  </span>
                )}
              </div>
            )}

            {isDiscontinued && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg">
                <AlertTriangle size={18} />
                <span className="font-medium">Property Discontinued</span>
              </div>
            )}

            {/* Helper Text */}
            {isOnboardingInProgress && !areAllOnboardingStepsCompleted() && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                <AlertTriangle size={16} />
                Complete all onboarding steps to mark as Onboarded
              </div>
            )}

            {isOffboarding && !areAllOffboardingStepsCompleted() && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                <AlertTriangle size={16} />
                Complete all offboarding steps to discontinue property
              </div>
            )}
          </div>
        </div>
      )}

      {/* Onboarding Workflow Section - No tabs, only onboarding shown */}
      {showOnboardingWorkflow && !isDiscontinued && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <div className="flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium bg-teal-50 text-teal-700 border-b-2 border-teal-600">
              <FileCheck size={18} />
              Onboarding Workflow
              {hotel.finalGoLiveStatus && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Completed</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workflow Panel — Phase 15.4: render Offboarding workflow when the
            property has moved into the offboarding phase. Previously this
            block ALWAYS rendered onboarding, even though the page header
            already said "Offboarding" — pure state-mixing bug. */}
        <div className="lg:col-span-2" data-testid={`workflow-panel-${activeWorkflow}`}>
          {isOffboarding || isDiscontinued || isOffboarded ? (
            <>
              <OffboardingWorkflowPanel
                hotel={
                  // Phase 19.1 — when the property is fully offboarded, force
                  // all 7 steps to render as COMPLETED, preserving any existing
                  // step metadata (assignee / completedAt / reason / docs).
                  isOffboarded
                    ? {
                        ...hotel,
                        offboardingSteps: Object.fromEntries(
                          OFFBOARDING_STEPS.map((s) => [
                            s.id,
                            {
                              ...(hotel.offboardingSteps?.[s.id] || {}),
                              status: STEP_STATUS.COMPLETED,
                              completedAt:
                                hotel.offboardingSteps?.[s.id]?.completedAt ||
                                hotel.offboardingCompletedAt ||
                                hotel.updatedAt,
                              autoSynced:
                                hotel.offboardingSteps?.[s.id]?.status !==
                                STEP_STATUS.COMPLETED,
                            },
                          ])
                        ),
                      }
                    : hotel
                }
                role={role}
                isAdmin={isAdmin}
                onAfterAction={loadHotelData}
              />
              {/* Phase 19.1 — Re-onboard 3-step approval, shown on fully
                  offboarded properties to bfr / cx_leader / executive / admin.
                  The card self-hides for other roles or non-offboarded states. */}
              <div className="mt-4">
                <ReonboardCard hotelId={hotel.id || hotel.propertyId} onRefresh={loadHotelData} />
              </div>
            </>
          ) : (
            <WorkflowContainer
              title="Onboarding Workflow"
              description="Complete all steps to successfully onboard this hotel"
              icon={FileCheck}
              steps={ONBOARDING_STEPS}
              stepsData={
                // If property is already onboarded (finalGoLiveStatus), ensure all steps show as completed
                hotel.finalGoLiveStatus
                  ? Object.fromEntries(
                      ONBOARDING_STEPS.map(step => [
                        step.id,
                        {
                          status: STEP_STATUS.COMPLETED,
                          ...(hotel.onboardingSteps?.[step.id] || {}),
                          // Force completed status for onboarded properties
                          ...(hotel.onboardingSteps?.[step.id]?.status !== STEP_STATUS.COMPLETED
                            ? { status: STEP_STATUS.COMPLETED, autoSynced: true }
                            : {})
                        }
                      ])
                    )
                  : (hotel.onboardingSteps || {})
              }
              onStartStep={handleStartStep}
              onCompleteStep={handleCompleteStep}
              onAssignUser={handleAssignUser}
              users={users}
              isAdmin={isAdmin}
              disabled={hotel.finalGoLiveStatus || isDiscontinued}
              color="teal"
            />
          )}
        </div>

        {/* Activity Log Panel */}
        <div className="lg:col-span-1">
          <ActivityLog activities={activities} />
          
          {/* Property Info Card */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Property Info</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Lead ID:</span>
                <span className="text-gray-800 font-medium">{hotel.leadId || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Property Status:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPropertyStatusColor()}`}>
                  {propertyStatus}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Onboarding:</span>
                <span className="text-gray-800">{hotel.onboardingStatus || 'Not Started'}</span>
              </div>
              {hotel.finalGoLiveAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Live Since:</span>
                  <span className="text-gray-800">
                    {new Date(hotel.finalGoLiveAt).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    })}
                  </span>
                </div>
              )}
              {hotel.offboardingCompletedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Discontinued:</span>
                  <span className="text-gray-800">
                    {new Date(hotel.offboardingCompletedAt).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Admin Controls - Delete/Restore */}
          {isAdmin && (
            <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Admin Controls</h4>
              <div className="space-y-2">
                {hotel.isDeleted ? (
                  <button
                    onClick={() => {/* restore logic */}}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                    data-testid="restore-hotel-btn"
                  >
                    <RotateCcw size={16} />
                    Restore Hotel
                  </button>
                ) : (
                  <button
                    onClick={() => {/* delete logic */}}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                    data-testid="delete-hotel-btn"
                  >
                    <Trash2 size={16} />
                    Delete Hotel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelLifecycleDetail;
